import React, { useState, useEffect, useRef } from 'react';
import { X, Search, BookOpen, Mail, Users, Settings as SettingsIcon, CreditCard, Share2, MessageCircle, Loader2, Play, ChevronRight, Sparkles, ExternalLink, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ToastContainer } from './Toast';
import { MarkdownRenderer } from '../lib/markdownRenderer';
import { walkthroughUrl, walkthroughPoster, WALKTHROUGHS, type Walkthrough } from '../data/walkthroughs';

interface KnowledgeArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  video_embed: string | null;
  walkthrough_token: string | null;
  category: string;
  view_count: number;
  created_at: string;
  updated_at: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const CATEGORIES = [
  { value: 'campaigns', label: 'Campagnes', icon: Mail },
  { value: 'blogs', label: 'Blogs', icon: BookOpen },
  { value: 'mailinglist', label: 'Mailinglijst', icon: Users },
  { value: 'social-media', label: 'Social Media', icon: Share2 },
  { value: 'account', label: 'Account', icon: SettingsIcon },
  { value: 'credits', label: 'Credits', icon: CreditCard }
];

type ViewMode = 'browse' | 'category' | 'article' | 'search' | 'support' | 'videos' | 'video';

export function KnowledgeOverlay() {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('browse');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  const [selectedWalkthrough, setSelectedWalkthrough] = useState<Walkthrough | null>(null);
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiAnswer, setAiAnswer] = useState<string>('');
  const [sourceArticles, setSourceArticles] = useState<KnowledgeArticle[]>([]);
  const [searchWalkthrough, setSearchWalkthrough] = useState<{ token: string; title: string; url: string } | null>(null);
  const [supportEmail, setSupportEmail] = useState('');
  const [supportName, setSupportName] = useState('');
  const [supportDescription, setSupportDescription] = useState('');
  const [supportUrgency, setSupportUrgency] = useState<'low' | 'normal' | 'high'>('normal');
  const [supportTag, setSupportTag] = useState<string | undefined>(undefined);
  const [requestingSupport, setRequestingSupport] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [toasts, setToasts] = useState<Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
  }>>([]);

  useEffect(() => {
    const getOrCreateSessionId = () => {
      let stored = localStorage.getItem('dibbi_chat_session');
      if (!stored) {
        stored = crypto.randomUUID();
        localStorage.setItem('dibbi_chat_session', stored);
      }
      return stored;
    };
    setSessionId(getOrCreateSessionId());
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    const handleOpenKnowledge = () => {
      setIsOpen(true);
      setViewMode('browse');
    };

    const handleOpenKnowledgeSupport = () => {
      setIsOpen(true);
      handleRequestSupport();
    };

    const handleOpenKnowledgeSupportWithText = async (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsOpen(true);
      setViewMode('support');

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: userData } = await supabase
          .from('users')
          .select('email, first_name')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (userData?.email) setSupportEmail(userData.email);
        if (userData?.first_name) setSupportName(userData.first_name);
      }

      if (customEvent.detail?.description) {
        setSupportDescription(customEvent.detail.description);
      }

      if (customEvent.detail?.tag) {
        setSupportTag(customEvent.detail.tag);
      }
    };

    document.addEventListener('keydown', handleEscape);
    window.addEventListener('openKnowledge', handleOpenKnowledge);
    window.addEventListener('openKnowledgeSupport', handleOpenKnowledgeSupport);
    window.addEventListener('openKnowledgeSupportWithText', handleOpenKnowledgeSupportWithText as EventListener);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('openKnowledge', handleOpenKnowledge);
      window.removeEventListener('openKnowledgeSupport', handleOpenKnowledgeSupport);
      window.removeEventListener('openKnowledgeSupportWithText', handleOpenKnowledgeSupportWithText as EventListener);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!selectedArticle?.walkthrough_token && selectedArticle?.video_embed && videoContainerRef.current) {
      const container = videoContainerRef.current;
      container.innerHTML = selectedArticle.video_embed;

      const scripts = container.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode?.replaceChild(newScript, oldScript);
      });
    }

    return () => {
      if (videoContainerRef.current) {
        videoContainerRef.current.innerHTML = '';
      }
    };
  }, [selectedArticle?.walkthrough_token, selectedArticle?.video_embed]);

  const fetchCategoryArticles = async (category: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_knowledge_base')
        .select('id, title, excerpt, video_embed, walkthrough_token, category, view_count, created_at, updated_at, content')
        .eq('category', category)
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setArticles(data || []);
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setViewMode('category');
    fetchCategoryArticles(category);
  };

  const handleArticleClick = (article: KnowledgeArticle) => {
    setSelectedArticle(article);
    setViewMode('article');
  };

  const handleBackToBrowse = () => {
    setViewMode('browse');
    setSelectedCategory(null);
    setSelectedArticle(null);
    setSelectedWalkthrough(null);
    setArticles([]);
    setSearchQuery('');
    setAiAnswer('');
    setSourceArticles([]);
    setSearchWalkthrough(null);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setViewMode('search');
    setLoading(true);
    setAiAnswer('');
    setSourceArticles([]);
    setSearchWalkthrough(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot-query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: searchQuery,
          userId: session?.user?.id,
          sessionId: sessionId,
          conversationId: conversationId,
          context: {
            url: window.location.pathname + window.location.search,
            pageTitle: document.title,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Chatbot request failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setAiAnswer(result.message);
        setSearchWalkthrough(result.walkthrough ?? null);

        if (result.matchedDocs && result.matchedDocs.length > 0) {
          const { data: articles } = await supabase
            .from('chat_knowledge_base')
            .select('*')
            .in('title', result.matchedDocs)
            .eq('is_published', true);

          setSourceArticles(articles || []);
        }
      } else {
        throw new Error(result.message || 'Onbekende fout bij zoeken');
      }
    } catch (err) {
      console.error('Error searching:', err);
      addToast('error', 'Zoeken mislukt', 'Er ging iets mis bij het ophalen van het antwoord. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSupport = async () => {
    setViewMode('support');

    // Vul de omschrijving vast voor met de vraag die de klant net stelde,
    // zodat support niet met halve informatie zit. Blijft aanpasbaar.
    if (searchQuery.trim() && !supportDescription.trim()) {
      setSupportDescription(searchQuery.trim());
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data: userData } = await supabase
        .from('users')
        .select('email, first_name')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();

      if (userData?.email) setSupportEmail(userData.email);
      if (userData?.first_name) setSupportName(userData.first_name);
    }
  };

  const addToast = (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, type, title, message }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleSubmitSupport = async () => {
    if (!supportEmail.trim() || !supportDescription.trim()) {
      addToast('warning', 'Vul je email en omschrijving in');
      return;
    }

    setRequestingSupport(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot-query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: supportDescription,
          conversationId,
          userId: session?.user?.id || null,
          sessionId: sessionId,
          requestSupport: true,
          userEmail: supportEmail.trim(),
          userName: supportName.trim() || null,
          ticketDescription: supportDescription.trim(),
          urgency: supportUrgency,
          tag: supportTag || null,
          conversationHistory: [],
          messageCount: 0,
          context: { url: window.location.href, pageTitle: document.title },
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!data?.supportTicketCreated) {
        // M4: alleen data.message tonen als de server echt op de supportvraag
        // heeft geantwoord (status ok en een supportTicketCreated-veld). Bij
        // een gateway-fout, een niet-JSON-antwoord, of een gewoon chatantwoord
        // (support staat uit) is data.message niet betrouwbaar.
        const isSupportAntwoord = response.ok && typeof data?.supportTicketCreated === 'boolean';
        addToast('error', 'Niet verstuurd', isSupportAntwoord ? data.message : 'Mail je vraag naar support@dibbi.nl.');
        return;
      }

      setSupportEmail('');
      setSupportName('');
      setSupportDescription('');
      setSupportUrgency('normal');
      setSupportTag(undefined);
      setIsOpen(false);
      addToast('success', 'Je vraag is binnen', data.message);

    } catch (error) {
      console.error('Support request error:', error);
      addToast('error', 'Er ging iets mis', 'Probeer het opnieuw.');
    } finally {
      setRequestingSupport(false);
    }
  };

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleBack = () => {
    if (viewMode === 'video') {
      setViewMode('videos');
      setSelectedWalkthrough(null);
    } else {
      handleBackToBrowse();
    }
  };

  const getCategoryInfo = (categoryValue: string) => {
    return CATEGORIES.find(cat => cat.value === categoryValue);
  };

  const categoryInfo = selectedCategory ? getCategoryInfo(selectedCategory) : null;

  const renderSidebar = () => (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      <div className="h-16 px-4 border-b border-gray-200 flex items-center">
        <h2 className="text-lg font-semibold text-gray-900">Kennisbank</h2>
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        <div className="space-y-0.5">
          {CATEGORIES.map((category) => {
            const Icon = category.icon;
            const isActive = selectedCategory === category.value;

            return (
              <button
                key={category.value}
                onClick={() => handleCategoryClick(category.value)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left">{category.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="p-2 border-t border-gray-200">
        <button
          onClick={() => { setSelectedCategory(null); setSelectedArticle(null); setViewMode('videos'); }}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
            viewMode === 'videos' || viewMode === 'video'
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <Play className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-left">Rondleidingen</span>
        </button>
        <button
          onClick={handleRequestSupport}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          <Mail className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-left">Support ticket</span>
        </button>
      </div>
    </div>
  );

  const renderBrowse = () => (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Hoe kunnen we je helpen?</h1>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
            Populaire onderwerpen
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.value}
                  onClick={() => handleCategoryClick(category.value)}
                  className="flex flex-col items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-gray-300 hover:shadow-sm transition-all"
                >
                  <Icon className="w-6 h-6 text-gray-700" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {category.label}
                    </h3>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={() => { setSelectedCategory(null); setSelectedArticle(null); setViewMode('videos'); }}
            className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-gray-300 hover:shadow-sm transition-all"
          >
            <Play className="w-6 h-6 text-gray-700 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Rondleidingen</h3>
              <p className="text-xs text-gray-500">Bekijk korte uitlegvideo's van dibbi</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderCategory = () => {
    if (!categoryInfo) return null;
    const CategoryIcon = categoryInfo.icon;

    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <CategoryIcon className="w-6 h-6 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">{categoryInfo.label}</h1>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
            </div>
          ) : articles.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Geen artikelen gevonden</p>
            </div>
          ) : (
            <div className="space-y-2">
              {articles.map((article) => (
                <button
                  key={article.id}
                  onClick={() => handleArticleClick(article)}
                  className="w-full bg-white border border-gray-200 rounded-lg p-4 text-left hover:bg-gray-50 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {article.title}
                        </h3>
                        {(article.walkthrough_token || article.video_embed) && (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                            <Play className="w-3 h-3" />
                            Video
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{article.excerpt}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderArticle = () => {
    if (!selectedArticle) return null;

    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto">
          <article className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-8">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full mb-4">
                  {categoryInfo?.label || selectedArticle.category}
                </span>
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  {selectedArticle.title}
                </h1>
              </div>

              {selectedArticle.walkthrough_token ? (
                <div className="mb-8">
                  <div className="relative w-full rounded-lg overflow-hidden bg-gray-900" style={{ paddingBottom: '56.25%', position: 'relative' }}>
                    <iframe
                      src={walkthroughUrl(selectedArticle.walkthrough_token)}
                      className="absolute inset-0 w-full h-full"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                      allow="autoplay; fullscreen; encrypted-media"
                      allowFullScreen
                      title={selectedArticle.title}
                    />
                  </div>
                </div>
              ) : selectedArticle.video_embed ? (
                <div className="mb-8">
                  <div className="relative w-full rounded-lg overflow-hidden bg-gray-900" style={{ paddingBottom: '56.25%', position: 'relative' }}>
                    <div
                      ref={videoContainerRef}
                      className="absolute inset-0 w-full h-full"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    />
                  </div>
                </div>
              ) : null}

              <div
                className="prose prose-base max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />
            </div>
          </article>
        </div>
      </div>
    );
  };

  const renderVideos = () => (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Rondleidingen</h1>
        <div className="grid grid-cols-2 gap-4">
          {WALKTHROUGHS.map((w) => (
            <button
              key={w.slug}
              onClick={() => { setSelectedWalkthrough(w); setViewMode('video'); }}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden text-left hover:border-gray-300 hover:shadow-sm transition-all"
            >
              <div className="relative w-full overflow-hidden rounded-t-lg bg-gray-900" style={{ paddingBottom: '56.25%' }}>
                <img
                  src={walkthroughPoster(w.token)}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  alt={w.title}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white ml-0.5" />
                  </div>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm font-semibold text-gray-900 mb-1">{w.title}</p>
                <p className="text-xs text-gray-500">{formatDuration(w.duration)}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderVideo = () => {
    if (!selectedWalkthrough) return null;
    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">{selectedWalkthrough.title}</h1>
          <div className="relative w-full rounded-lg overflow-hidden bg-gray-900" style={{ paddingBottom: '56.25%', position: 'relative' }}>
            <iframe
              src={walkthroughUrl(selectedWalkthrough.token)}
              className="absolute inset-0 w-full h-full"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="autoplay; fullscreen; encrypted-media"
              allowFullScreen
              title={selectedWalkthrough.title}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSearch = () => (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <p className="text-gray-600">Voor "{searchQuery}"</p>
        </div>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
            <p className="text-gray-500 mt-2">AI genereert antwoord...</p>
          </div>
        ) : aiAnswer ? (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      AI-antwoord
                    </h3>
                    <p className="text-sm text-gray-600">
                      Automatisch gegenereerd door een AI-assistent op basis van onze kennisbank. Antwoorden kunnen fouten bevatten, controleer belangrijke informatie.
                    </p>
                  </div>
                </div>
                <MarkdownRenderer
                  content={aiAnswer}
                  onSupportButtonClick={handleRequestSupport}
                />
              </div>
            </div>

            {searchWalkthrough && (
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Bekijk de rondleiding
                </h3>
                <p className="text-sm text-gray-600 mb-4">{searchWalkthrough.title}</p>
                <div className="relative w-full rounded-lg overflow-hidden bg-gray-900" style={{ paddingBottom: '56.25%', position: 'relative' }}>
                  <iframe
                    src={searchWalkthrough.url}
                    className="absolute inset-0 w-full h-full"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    allow="autoplay; fullscreen; encrypted-media"
                    allowFullScreen
                    title={searchWalkthrough.title}
                  />
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    Nog niet gevonden wat je zocht?
                  </h3>
                  <p className="text-sm text-gray-600">
                    Ons support team helpt je graag persoonlijk verder
                  </p>
                </div>
                <button
                  onClick={handleRequestSupport}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Maak support ticket
                </button>
              </div>
            </div>

            {sourceArticles.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Bronnen ({sourceArticles.length})
                </h3>
                <div className="space-y-2">
                  {sourceArticles.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => handleArticleClick(article)}
                      className="w-full bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-blue-300 hover:shadow-sm transition-all"
                    >
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">
                        {article.title}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {article.excerpt}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderSupport = () => (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Maak een support ticket aan</h2>
          <p className="text-sm text-gray-600 mb-6">
            Vul je gegevens in zodat ons support team contact met je kan opnemen.
          </p>

          <div className="space-y-4">
            <input
              type="email"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              placeholder="Email adres *"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={requestingSupport}
            />
            <input
              type="text"
              value={supportName}
              onChange={(e) => setSupportName(e.target.value)}
              placeholder="Naam (optioneel)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={requestingSupport}
            />
            <textarea
              value={supportDescription}
              onChange={(e) => setSupportDescription(e.target.value)}
              placeholder="Beschrijf je vraag of probleem *"
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              disabled={requestingSupport}
            />
            <select
              value={supportUrgency}
              onChange={(e) => setSupportUrgency(e.target.value as 'low' | 'normal' | 'high')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={requestingSupport}
            >
              <option value="low">Lage urgentie</option>
              <option value="normal">Normale urgentie</option>
              <option value="high">Hoge urgentie</option>
            </select>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setViewMode('browse')}
                disabled={requestingSupport}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium"
              >
                Annuleren
              </button>
              <button
                onClick={handleSubmitSupport}
                disabled={requestingSupport || !supportEmail.trim() || !supportDescription.trim()}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
              >
                {requestingSupport ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  'Verstuur ticket'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) {
    return <ToastContainer toasts={toasts} onClose={removeToast} />;
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-50 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />
      <div className="fixed inset-8 bg-white rounded-xl shadow-2xl z-50 flex overflow-hidden">
        {renderSidebar()}

        <div className="flex-1 flex flex-col">
          <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
            {viewMode !== 'browse' && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Terug
              </button>
            )}

            {viewMode === 'category' && categoryInfo && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mr-4">
                <ChevronRight className="w-4 h-4" />
                <span className="font-medium text-gray-900">{categoryInfo.label}</span>
              </div>
            )}

            {viewMode === 'article' && selectedArticle && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mr-4">
                <ChevronRight className="w-4 h-4" />
                <span className="font-medium text-gray-900">{selectedArticle.title}</span>
              </div>
            )}

            {viewMode === 'search' && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mr-4">
                <ChevronRight className="w-4 h-4" />
                <span className="font-medium text-gray-900">Zoekresultaten</span>
              </div>
            )}

            {viewMode === 'support' && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mr-4">
                <ChevronRight className="w-4 h-4" />
                <span className="font-medium text-gray-900">Support ticket</span>
              </div>
            )}

            {viewMode === 'videos' && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mr-4">
                <ChevronRight className="w-4 h-4" />
                <span className="font-medium text-gray-900">Rondleidingen</span>
              </div>
            )}

            {viewMode === 'video' && selectedWalkthrough && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mr-4">
                <ChevronRight className="w-4 h-4" />
                <span className="font-medium text-gray-900">{selectedWalkthrough.title}</span>
              </div>
            )}

            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Zoek in de kennisbank..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
              className="ml-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Zoeken
            </button>

            <button
              onClick={() => setIsOpen(false)}
              className="ml-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {viewMode === 'browse' && renderBrowse()}
          {viewMode === 'category' && renderCategory()}
          {viewMode === 'article' && renderArticle()}
          {viewMode === 'search' && renderSearch()}
          {viewMode === 'support' && renderSupport()}
          {viewMode === 'videos' && renderVideos()}
          {viewMode === 'video' && renderVideo()}
        </div>
      </div>
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  );
}
